/**
 * Canonical entity URLs
 *
 * `https://lihockey.org/game/591` is the only form of a game link that unfurls
 * in Discord / iMessage / Slack — it is a baked S3 page carrying real Open
 * Graph tags (see SOCIAL_LINK_PREVIEWS.md). That page hands real browsers to
 * `/game.html?game_id=591`, which meant the address bar — and therefore
 * anything a member copied and re-shared — ended up on the preview-less form.
 *
 * This module closes the loop from the app side: it resolves the entity id from
 * EITHER url form, and rewrites the address bar to the pretty one.
 *
 * The single hazard worth understanding: `/game/{id}` DOES NOT EXIST for every
 * game. The API only bakes a page for a published, completed, scored game
 * (`shouldHaveGamePreview` in api/utils/gamePreview.js); a scheduled game's
 * pretty URL is a 404. Same for players and teams. So the rewrite is gated on
 * proof that the page exists, established cheapest-first:
 *
 *   1. document.referrer is the pretty URL  → the preview page is literally
 *      what sent us here, so it exists. Free, and instant.
 *   2. HEAD the pretty URL → 200 means it exists. One ~0-byte CloudFront-cached
 *      request, only needed when someone opened a legacy `?game_id=` link.
 *
 * Anything else — probe fails, page missing, fetch blocked, file:// — leaves the
 * url exactly as it was, which is always a working link.
 *
 * Exposes `window.LIHUrl`:
 *   .type          'game' | 'player' | 'team' | null
 *   .id            resolved id string, or null
 *   .canonicalPath '/game/591', or null
 *   .canonical     true once the address bar is on the pretty form
 *   .shareUrl()    absolute url to hand to a share sheet / clipboard
 */
(function () {
    'use strict';

    // The three entity pages that have baked preview pages. Keyed by the first
    // path segment, which is the same word in all three url forms:
    // /game.html?game_id=1, /game?game_id=1, /game/1
    var ENTITIES = {
        game: 'game_id',
        player: 'player_id',
        team: 'team_id'
    };

    var segments = window.location.pathname.replace(/^\/+/, '').split('/');
    var type = segments[0].replace(/\.html$/, '');
    var param = ENTITIES[type];

    var api = {
        type: param ? type : null,
        id: null,
        canonicalPath: null,
        canonical: false,
        shareUrl: function () {
            return window.location.origin + (
                api.canonical && api.canonicalPath
                    ? api.canonicalPath
                    : window.location.pathname + window.location.search
            );
        }
    };
    window.LIHUrl = api;

    if (!param) return;

    // A numeric second segment means we are already on the pretty path. Note
    // this is also true AFTER we rewrite: the document is still game.html, but
    // location.pathname reads /game/591 — which is why every id read has to go
    // through here rather than through location.search.
    var pathId = /^\d+$/.test(segments[1] || '') ? segments[1] : null;
    var search = new URLSearchParams(window.location.search);

    api.id = pathId || search.get(param);
    if (!api.id) return;

    api.canonicalPath = '/' + type + '/' + api.id;

    if (pathId) {
        api.canonical = true;
        return;
    }

    /**
     * Swap the address bar over to the pretty path, carrying every OTHER query
     * param (?event_id=3578 deep links) and the hash. replaceState rather than
     * pushState: the trip through the preview page is plumbing, not a step the
     * back button should have to walk back through.
     */
    function canonicalize() {
        if (api.canonical) return;
        var rest = new URLSearchParams(window.location.search);
        rest.delete(param);
        var query = rest.toString();
        try {
            window.history.replaceState(
                window.history.state,
                '',
                api.canonicalPath + (query ? '?' + query : '') + window.location.hash
            );
            api.canonical = true;
        } catch (e) {
            // replaceState throws on file:// and in a few locked-down webviews.
            // Staying on the legacy url is a perfectly good outcome.
        }
    }

    // 1. Did the preview page send us here? Then it exists — no request needed.
    // Same-origin referrers carry the full path under the default policy.
    var referrer = document.referrer || '';
    if (referrer.indexOf(window.location.origin + api.canonicalPath) === 0) {
        var tail = referrer.charAt(window.location.origin.length + api.canonicalPath.length);
        if (tail === '' || tail === '?' || tail === '#') {
            canonicalize();
            return;
        }
    }

    // 2. Someone opened a legacy `?game_id=` link directly. Ask whether a
    // preview page exists before claiming its url. A 404 here is the right
    // answer for a scheduled game and costs nothing to be wrong about.
    if (window.location.protocol === 'file:' || typeof window.fetch !== 'function') return;
    try {
        window.fetch(api.canonicalPath, { method: 'HEAD', credentials: 'omit' })
            .then(function (response) {
                if (response && response.ok) canonicalize();
            })
            .catch(function () { /* offline, blocked, whatever — keep the legacy url */ });
    } catch (e) { /* ditto */ }
})();
